import { defineStore } from "pinia";
import {
  VdpInitSubmissionModel,
  VdpPermitDetailModel,
  VdpValidationData,
  VdpSubmissionFetchModel,
  VdpPointOfContactModel
} from "@ntprsrf-frontend/service/src/models/internet/vdp.models";
import type { SubmittedDocument } from "@ntprsrf-frontend/internet/src/views/GovernmentServices/Vdp/components/types";
import type { FileItem } from '@ntprsrf-frontend/common/src/utils/schema';
import type { VdpUploadSupportDocsResponse } from '@ntprsrf-frontend/service/src/responses/internet/vdp.response';

const permitDetailsPrefix = 'permitDetails';

export const useVdpStore = defineStore('vdp', {
  state: () => ({
    draftCount: null as null | number,
    lastGroupNo: 1,
    draftDetails: null as VdpInitSubmissionModel | null,
    submissionDetails: null as VdpSubmissionFetchModel | null,
    permits: [] as VdpPermitDetailModel[],
    currentPermit: null as VdpPermitDetailModel | null,
    pointOfContactDetails: null as VdpPointOfContactModel | null,
    backendValidationResponse: null as VdpValidationData | null,
    readOnlyMode: false,
    sharedUploadedFilesByKey: {} as Record<string, FileItem[]>,
    sharedUploadResultsByKey: {} as Record<string, VdpUploadSupportDocsResponse[]>,
    requiredDocTypesCache: {} as Record<string, Record<string, string>>,
    permitDocumentMappings: {} as Record<string, SubmittedDocument[]>,
  }),
  getters: {
    getIsValidated: (state) => state.backendValidationResponse != null,
    getDraftCount: (state) => state.draftCount ?? 0,
    isDraftLimitReached: (state) => (state.draftCount ?? 0) >= 10,
    getDraftDetails: (state) => state.draftDetails,
    getSubmissionDetails: (state) => state.submissionDetails,
    getPermitById: (state) => (permitId: string) => {
      return state.permits.find(p => p.permitId === permitId);
    },
    getPermits: (state) => state.permits,
    getPointOfContactDetails: (state) => state.pointOfContactDetails,
    getBackendValidationResponse: (state) => state.backendValidationResponse,
    getReadOnlyMode: (state) => state.readOnlyMode,
    curPermitIdx: (state) => {
      const permitId = state.currentPermit?.permitId;
      if (!permitId) return 0;
      if (!permitId.startsWith(permitDetailsPrefix)) return 0;

      const permitNumber = Number.parseInt(permitId.slice(permitDetailsPrefix.length), 10);
      return Number.isNaN(permitNumber) ? 0 : permitNumber - 1;
    },
    getRequiredDocTypes: (state) => (cacheKey: string) => {
      return state.requiredDocTypesCache[cacheKey] || null;
    },
    getPermitDocumentMappings: (state) => (permitId: string) => {
      return state.permitDocumentMappings[permitId] || [];
    },
    getSharedUploadedFiles: (state) => (key = 'permit') => {
      return state.sharedUploadedFilesByKey[key] || [];
    },
    getSharedUploadResults: (state) => (key = 'permit') => {
      return state.sharedUploadResultsByKey[key] || [];
    },
    getFileDocTypeLocks: (state) => (excludePermitId?: string): Record<string, string> => {
      return Object.entries(state.permitDocumentMappings)
        .filter(([permitId]) => permitId !== excludePermitId)
        .flatMap(([, mappings]) => mappings)
        .flatMap(mapping => mapping.files.map(file => ({ fileId: file.id, docTypeId: mapping.documentTypeId })))
        .reduce((locks, { fileId, docTypeId }) => {
          if (!locks[fileId]) {
            locks[fileId] = docTypeId;
          }
          return locks;
        }, {} as Record<string, string>);
    },
    canSaveDraft: (state): boolean => {
      if (state.permits.some(p => p.errorType?.trim())) return true;
      const poc = state.pointOfContactDetails;
      if (poc?.companyPhone != null && String(poc?.companyPhone).trim()) return true;
      if (poc?.companyPocList?.some(c =>
        c.contactUserName?.trim() || c.contactUserEmail?.trim() ||
        c.contactUserPhone?.trim() || c.contactUserDesignation?.trim()
      )) return true;
      return false;
    },
  },
  actions: {
    setCurrentPermit(permit: VdpPermitDetailModel | null) {
      this.currentPermit = permit;
    },
    incrementGroupNo() {
      this.lastGroupNo += 1;
      return this.lastGroupNo;
    },
    setLastGroupNo(groupNo: number) {
      this.lastGroupNo = groupNo;
    },
    setDraftCount(count: number) {
      this.draftCount = count;
    },
    setDraftDetails(details: VdpInitSubmissionModel | null) {
      this.draftDetails = details;
    },
    setPermitDetails(permits: VdpPermitDetailModel) {
      const index = this.permits.findIndex(p => p.permitId === permits.permitId);
      if (index !== -1) {
        this.permits[index] = permits;
      } else {
        this.permits.push(permits);
      }
    },
    deletePermit(permitId: string) {
      const index = this.permits.findIndex(p => p.permitId === permitId);
      if (index !== -1) {
        const oldPermitIds = this.permits
          .filter(p => p.permitId !== permitId)
          .map(p => p.permitId);

        this.permits = this.permits
          .filter(p => p.permitId !== permitId)
          .map((permit, idx) => ({
            ...permit,
            permitId: `permitDetails${idx + 1}`
          }));
        const newMappings: Record<string, SubmittedDocument[]> = {};
        oldPermitIds.forEach((oldId, idx) => {
          const newId = `permitDetails${idx + 1}`;
          if (this.permitDocumentMappings[oldId]) {
            newMappings[newId] = this.permitDocumentMappings[oldId];
          }
        });
        this.permitDocumentMappings = newMappings;

        if (this.currentPermit?.permitId === permitId) {
          this.currentPermit = null;
        }
      }
    },
    setPointOfContactDetails(details: VdpPointOfContactModel | null) {
      this.pointOfContactDetails = details;
    },
    setReadOnlyMode(mode: boolean) {
      this.readOnlyMode = mode;
    },
    setBackendValidationResponse(response: VdpValidationData | null) {
      this.backendValidationResponse = response;
    },
    setSubmissionDetails(details: VdpSubmissionFetchModel | null) {
      this.submissionDetails = details;
    },
    setCtaExtended(ctaActionDataId: string, extended: boolean, newDate: string) {
      if (this.submissionDetails) {
        const curCta = this.submissionDetails.payload.ctas.find(cta => cta.ctaActionDataId === ctaActionDataId);
        if (curCta) {
          curCta.extended = extended;
          curCta.ctaDeadline = newDate;
        }
      }
    },
    clearTabError(path: string) {
      if (!this.backendValidationResponse) return;

      // Deep clone
      const cloned: any = { ...this.backendValidationResponse } 

      const keys = path.split(".");
      let current: any = cloned;

      // Traverse to parent
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) return;
        current = current[keys[i]];
      }

      const targetKey = keys[keys.length - 1];

      // Get error count to subtract
      const removedCount = current[targetKey]?.accumulatedErrorCount ?? 0;

      // Delete node
      delete current[targetKey];

      // Update root error count
      cloned[keys[0]].accumulatedErrorCount =
        (cloned[keys[0]].accumulatedErrorCount || 0) - removedCount;

      // Replace state
      this.backendValidationResponse = cloned;
    },
    clearFieldError(path: string) {
      if (!this.backendValidationResponse) return;
      
      const keys = path.split('.');
      let current: any = this.backendValidationResponse;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (current == null) return;
        current = current[keys[i]];
      }
      // Get the last key
      const lastKey = keys[keys.length - 1];
      if (current?.[lastKey]) {
        // Clear the error for this specific field
        current[lastKey] = {
          ...current[lastKey],
          currentLevelValid: true,
          currentLevelErrorMessage: null,
        };
      }
    },
    clearDraftDetails() {
      this.draftDetails = null;
    },
    initDocumentMappingFromExistingData() {
      this.permits.forEach((permit) => {
        const mapping = Object.entries(permit.error.supportedDocsTagMap).map(([documentTypeId, documentTypeName]) => {
          const taggedFileNames = permit.error.supportedDocsTagged?.[documentTypeId];
          if (!taggedFileNames) {
            return { documentType: documentTypeName, documentTypeId, files: [], required: true };
          }

          const files = taggedFileNames.map((name) => {
            const existingFile = this.getSharedUploadedFiles('permit').find(
              f => f.file.name.toLowerCase() === name.toLowerCase()
            );
            return {
              id: existingFile ? existingFile._id : `${documentTypeId}-${name}`,
              name
            };
          });

          return { documentType: documentTypeName, documentTypeId, files, required: true };
        });

        this.setPermitDocumentMappings(permit.permitId, mapping);
      })
    },
    setPermitDocumentMappings(permitId: string, mappings: SubmittedDocument[]) {
      this.permitDocumentMappings[permitId] = mappings;
    },
    addSharedUploads(uploadedFiles: FileItem[], uploadResults?: VdpUploadSupportDocsResponse[]) {
      this.addSharedUploadsByKey('permit', uploadedFiles, uploadResults);
    },
    addSharedUploadsByKey(scopeKey: string, uploadedFiles: FileItem[], uploadResults?: VdpUploadSupportDocsResponse[]) {
      const currentFiles = this.sharedUploadedFilesByKey[scopeKey] || [];
      const existingFileIds = new Set(currentFiles.map(f => f._id));
      const nextFiles = [...currentFiles];

      uploadedFiles.forEach(f => {
        if (!existingFileIds.has(f._id)) {
          nextFiles.push(f);
        }
      });
      this.sharedUploadedFilesByKey[scopeKey] = nextFiles;

      if (uploadResults) {
        const currentResults = this.sharedUploadResultsByKey[scopeKey] || [];
        const existingPhysicalNames = new Set(
          currentResults.flatMap(r =>
            Array.isArray(r.data) ? r.data.map(d => d.physicalFileName) : []
          )
        );
        const nextResults = [...currentResults];

        uploadResults.forEach(result => {
          if (Array.isArray(result.data) && !result.data.some(d => existingPhysicalNames.has(d.physicalFileName))) {
            nextResults.push(result);
          }
        });
        this.sharedUploadResultsByKey[scopeKey] = nextResults;
      }
    },
    removeSharedUpload(fileId: string) {
      this.removeSharedUploadByKey('permit', fileId);
    },
    removeSharedUploadByKey(scopeKey: string, fileId: string) {
      const fileName = (this.sharedUploadedFilesByKey[scopeKey] || []).find(f => f._id === fileId)?.file?.name;
      this.sharedUploadedFilesByKey[scopeKey] = (this.sharedUploadedFilesByKey[scopeKey] || []).filter(f => f._id !== fileId);
      Object.keys(this.permitDocumentMappings).forEach(permitId => {
        this.permitDocumentMappings[permitId] = this.permitDocumentMappings[permitId]
          .map(mapping => ({
            ...mapping,
            files: mapping.files.filter(f => f.id !== fileId),
          }))
          .filter(mapping => mapping.files.length > 0);
      });

      // remove supportedDocsTagged from the permit
      if (scopeKey === 'permit' && fileName) {
        this.permits.forEach(permit => {
          const tagged = permit.error.supportedDocsTagged;
          let newTag: Record<string, string[]> = {};
          for (const key in tagged) {
            if (tagged[key].includes(fileName)) {
              newTag[key] = tagged[key].filter((x) => {
                return x !== fileName;
              });
            } else {
              newTag[key] = tagged[key];
            }
          }
          permit.error.supportedDocsTagged = newTag;
        });
      }
    },
    setRequiredDocTypes(cacheKey: string, docTypes: Record<string, string>) {
      this.requiredDocTypesCache[cacheKey] = docTypes;
    },
    resetStore() {
      this.draftCount = null;
      this.draftDetails = null;
      this.permits = [];
      this.currentPermit = null;
      this.backendValidationResponse = null;
      this.lastGroupNo = 1;
      this.readOnlyMode = false;
      this.submissionDetails = null;
      this.sharedUploadedFilesByKey = {};
      this.sharedUploadResultsByKey = {};
      this.requiredDocTypesCache = {};
      this.permitDocumentMappings = {};
    },
  },
});
